import React, { useCallback, useMemo, useState } from 'react';
import { CheckOutlined, CloseOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, Card, message, Popconfirm, Table } from 'antd';
import { usePaginatedQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import AvatarStatus from 'componentsv2/AvatarStatus/AvatarStatus';
import { changeLineupRequestStatus, getLineupRequests } from 'api/teams';

const statuses = {
  accepted: {
    status: 'accepted',
    successMessage: '¡Miembro aprobado con éxito!',
  },
  rejected: {
    status: 'rejected',
    successMessage: '¡Miembro rechazado con éxito!',
  },
};

const LineupRequests = () => {
  const { teamId, tournamentId } = useParams();

  const tableColumns = [
    {
      title: '# de Jugador',
      sorter: (a, b) => a?.number - b?.number,
      render: (_, record) => (record?.number ? `${record?.number}` : 'Sin número'),
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      render: (_, record) => (
        <div className="d-flex">
          <AvatarStatus
            src={record?.club_member.user.avatar_url}
            name={`${record?.club_member.user.first_name} ${record?.club_member.user.last_name}`}
          />
        </div>
      ),
      sorter: {
        compare: (a, b) => {
          a = a.club_member.user.first_name.toLowerCase();
          b = b.club_member.user.first_name.toLowerCase();
          return a > b ? -1 : b > a ? 1 : 0;
        },
      },
    },
    {
      title: 'Correo Electrónico',
      dataIndex: ['user', 'email'],
      render: (_, record) => record?.club_member.user.email,
      sorter: {
        compare: (a, b) => {
          a = a.club_member.user.email.toLowerCase();
          b = b.club_member.user.email.toLowerCase();
          return a > b ? -1 : b > a ? 1 : 0;
        },
      },
    },
    {
      title: 'Nombre de Usuario',
      dataIndex: ['user', 'user_name'],
      render: (_, record) => record?.club_member.user.user_name,
      sorter: {
        compare: (a, b) => {
          a = a.club_member.user.user_name.toLowerCase();
          b = b.club_member.user.user_name.toLowerCase();
          return a > b ? -1 : b > a ? 1 : 0;
        },
      },
    },
    {
      title: '',
      dataIndex: 'actions',
      render: (_, { id }) => (
        <div className="flex">
          <div className="text-right">
            <Popconfirm
              title="¿Deseas aprobar este miembro?"
              onConfirm={() => onConfirm(id, 'accepted')}
              okText="Aprobar"
              cancelText="Cancelar"
            >
              <Button type="primary" className="mr-2" icon={<CheckOutlined />} size="small" />
            </Popconfirm>
          </div>
          <div className="text-right">
            <Popconfirm
              title="¿Deseas rechazar este miembro?"
              onConfirm={() => onConfirm(id, 'rejected')}
              okText="Rechazar"
              cancelText="Cancelar"
            >
              <Button
                type="primary"
                danger
                className="mr-2"
                icon={<CloseOutlined />}
                size="small"
              />
            </Popconfirm>
          </div>
        </div>
      ),
    },
  ];

  const [page, setPage] = useState(1);

  const resolver = useCallback(
    async (_, _tournamentId, _teamId) => {
      const { body } = await getLineupRequests(_tournamentId, _teamId, page);

      return {
        clubName: body?.data?.[0]?.club_member?.club?.name || '',
        data: body?.data || [],
        hasMore: Boolean(body?.pagination?.next) || false,
        total: body?.pagination?.count,
      };
    },
    [page],
  );

  const { status, resolvedData, refetch } = usePaginatedQuery(
    ['lineup-requests', tournamentId, teamId, page],
    resolver,
  );

  const onConfirm = async (lineupRequestId, status) => {
    try {
      await changeLineupRequestStatus(tournamentId, lineupRequestId, statuses[status].status);
      message.success(statuses[status].successMessage);
      refetch();
    } catch (error) {
      message.error('!Ha ocurrido un error!');
    }
  };

  const requests = useMemo(() => resolvedData?.data || [] || [], [resolvedData]);

  const paginationConfig = {
    hideOnSinglePage: true,
    total: resolvedData?.total,
    showSizeChanger: false,
    defaultPageSize: 9,
    onChange: page => setPage(page),
  };

  const isLoading = useMemo(() => status === 'loading', [status]);

  return (
    <Card
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingBottom: '16px',
          }}
        >
          <TeamOutlined /> : {resolvedData?.clubName}
        </div>
      }
      bodyStyle={{ padding: '0px' }}
    >
      <Table
        columns={tableColumns}
        dataSource={requests}
        loading={isLoading}
        pagination={paginationConfig}
        rowKey={obj => obj?.id}
      />
    </Card>
  );
};

export default LineupRequests;
